import {
  V2_TIER_BY_TRIAL_PLAN,
  getPlanTerms,
  isV2TrialPlan,
  type BillingPlan,
  type BillingPlanV2Tier,
  type LineupPlanId,
  type PaywallState,
  type SubscriptionInfo,
} from '@highlight/common'
import { isLegacyPricingPlan, isPlanEnded, v2TierFor } from '@renderer/chat/utils/billingUtils'
import {
  briefsOutCopy,
  combinedLimitsCopy,
  creditsOutCopy,
  followUpsOutCopy,
  formatCount,
  hoursOutCopy,
  resetDateLabel,
  type LimitCopyContext,
  type MonthlyLimit,
} from '@renderer/components/pricing/limits-copy'
import type { TopUpKind } from '@renderer/components/pricing/top-up-kinds'
import type { SidepanelPaywallRequest } from '../sidepanel-paywall-request'
import { docksTrialEnding, resolveTopUpStrips } from '../sidepanel-paywall-strips'

// Pure module, no atoms or React, so the banner rules stay testable.

/** Credits warn once a tenth of the allowance is left: 50 on Free, 800 on Pro, 6,000 on Max. */
export const LOW_CREDITS_FRACTION = 0.1
/** Meeting hours warn with an hour left on every plan. */
export const LOW_MEETING_HOURS = 1

/** Most urgent first. The sidepanel shows the first one not dismissed. */
export const PLAN_BANNER_KINDS = [
  'plan-ended',
  'trial-ending',
  'trial-ended',
  'limits-out',
  'hours-out',
  'credits-out',
  'briefs-out',
  'follow-ups-out',
  'hours-low',
  'credits-low',
] as const

export type PlanBannerKind = (typeof PLAN_BANNER_KINDS)[number]

/**
 * What the banner's action does. Free can't buy top-ups, so every Free limit
 * sells an upgrade; a paid tier refills with a top-up instead.
 */
export type PlanBannerAction = 'upgrade' | 'top-up' | 'keep' | 'renew'

export interface PlanBanner {
  kind: PlanBannerKind
  action: PlanBannerAction
  /** The plan the action sells or refills, which also colours it. */
  ctaPlan: BillingPlanV2Tier
  /** What a top-up action buys, and the second one beside it when two allowances are spent. */
  topUp?: TopUpKind
  secondaryTopUp?: TopUpKind
  /** The allowances a `limits-out` banner names, two or more. */
  limits?: Array<MonthlyLimit>
  /** Free's monthly follow-ups, which a `follow-ups-out` banner quotes. */
  followUpLimit?: number
  /**
   * Names this occurrence. A dismissal lasts until the key changes: the next
   * period, a limit going from low to spent, or a new trial end date.
   */
  dismissKey: string
}

export interface PlanBannerInputs {
  paywall: PaywallState
  subscription: SubscriptionInfo
  /** The plan before this one, so Free can tell it came from a trial. */
  previousPlan: BillingPlan | undefined
  request: SidepanelPaywallRequest | null
  /** UnifiedBillingState.lastUpdated, which retires a request the meters contradict. */
  billingUpdatedAt: number
  /** The period end a brief refusal was recorded in, or null. */
  briefLimitPeriodEnd: string | null
  /** Free's briefs left this period, or null when the count isn't known. */
  briefsLeft?: number | null
  /** Free's follow-ups this period, or null when the count isn't known. */
  followUps?: { limit: number; remaining: number } | null
  now?: number
}

/** The tier Free is upgraded to. Lite is folded into Pro on the revamp. */
const FREE_UPGRADE_TIER: BillingPlanV2Tier = 'pro2'

/**
 * Every banner the account qualifies for on the revamp, most urgent first.
 * The caller drops the dismissed ones and shows the first left.
 */
export function resolvePlanBanners(inputs: PlanBannerInputs): Array<PlanBanner> {
  const { paywall, subscription, previousPlan, request, billingUpdatedAt, briefLimitPeriodEnd, now } = inputs
  const { briefsLeft = null, followUps = null } = inputs
  if (paywall.isLoading || !paywall.plan || isLegacyPricingPlan(paywall.plan)) {
    return []
  }
  const tier = v2TierFor(paywall.plan)
  const isFree = paywall.plan === 'free'
  if (!tier && !isFree) {
    return []
  }
  const period = subscription.currentPeriodEndAt ?? ''
  const limitAction: PlanBannerAction = tier ? 'top-up' : 'upgrade'
  const limitPlan = tier ?? FREE_UPGRADE_TIER
  const banners: Array<PlanBanner> = []
  const add = (
    kind: PlanBannerKind,
    action: PlanBannerAction,
    ctaPlan: BillingPlanV2Tier,
    occurrence = period,
    extra: Partial<PlanBanner> = {},
  ) => banners.push({ kind, action, ctaPlan, dismissKey: `${kind}:${occurrence}`, ...extra })
  const topUpOf = (kind: TopUpKind) => (tier ? { topUp: kind } : {})

  if (tier && isPlanEnded(subscription)) {
    add('plan-ended', 'renew', tier)
  }
  const trialEnding = docksTrialEnding(paywall, subscription, now)
  if (trialEnding) {
    add('trial-ending', 'keep', trialEnding.tier, paywall.trialEndsAt)
  }
  if (isFree && previousPlan && isV2TrialPlan(previousPlan)) {
    const trialTier = V2_TIER_BY_TRIAL_PLAN[previousPlan]
    // Lite is off the revamp's lineup, so a Lite trial that ended before the flip has nothing to sell back.
    // One occurrence per trial: Continue on Free settles it for good.
    if (trialTier !== 'lite') {
      add('trial-ended', 'upgrade', trialTier, previousPlan)
    }
  }

  const { strips } = resolveTopUpStrips({ paywall, subscription, request, billingUpdatedAt })
  const spent = new Set(strips.map((strip) => strip.kind))
  const briefsSpent = isFree && ((briefLimitPeriodEnd !== null && briefLimitPeriodEnd === period) || briefsLeft === 0)
  // Named in the copy sheet's order, which the combined banner's key follows too.
  const spentLimits: Array<MonthlyLimit> = [
    ...(spent.has('credits') ? (['credits'] as const) : []),
    ...(spent.has('hours') ? (['hours'] as const) : []),
    ...(briefsSpent ? (['briefs'] as const) : []),
  ]
  // Two limits at once read as one banner, not a queue of them.
  if (spentLimits.length > 1) {
    add('limits-out', limitAction, limitPlan, `${spentLimits.join('+')}:${period}`, {
      limits: spentLimits,
      ...(tier ? { topUp: 'credits', secondaryTopUp: 'hours' } : {}),
    })
  } else {
    if (spent.has('hours')) add('hours-out', limitAction, limitPlan, period, topUpOf('hours'))
    if (spent.has('credits')) add('credits-out', limitAction, limitPlan, period, topUpOf('credits'))
    if (briefsSpent) add('briefs-out', 'upgrade', FREE_UPGRADE_TIER)
  }
  if (isFree && followUps?.remaining === 0) {
    add('follow-ups-out', 'upgrade', FREE_UPGRADE_TIER, period, { followUpLimit: followUps.limit })
  }

  // A warning needs something left: an empty meter the strips don't call spent (an ended plan, a
  // meter that failed to load) isn't "running low".
  const { meetingHours, credits } = paywall
  const hoursLeft = meetingHours.remaining ?? 0
  if (!spent.has('hours') && hoursLeft > 0 && hoursLeft <= LOW_MEETING_HOURS) {
    add('hours-low', limitAction, limitPlan, period, topUpOf('hours'))
  }
  if (!spent.has('credits') && credits.remaining > 0 && credits.remaining <= credits.total * LOW_CREDITS_FRACTION) {
    add('credits-low', limitAction, limitPlan, period, topUpOf('credits'))
  }
  return banners
}

/** The first banner not yet dismissed, which is the one the sidepanel shows. */
export function nextPlanBanner(banners: Array<PlanBanner>, dismissed: ReadonlyArray<string>): PlanBanner | null {
  return banners.find((banner) => !dismissed.includes(banner.dismissKey)) ?? null
}

export interface PlanBannerCopy {
  title: string
  body: string
  /** Absent on a banner that only informs, which then shows no action row. */
  cta?: string
  /** A second action beside the first, such as Buy hours beside Buy credits. */
  secondaryCta?: string
  /** A second way out that also dismisses the banner, such as Continue on Free. */
  stay?: string
}

/** The figures a banner quotes: the current plan's and the upgrade's monthly allowances. */
interface Allowances {
  hours: string
  credits: string
}

function allowancesOf(plan: LineupPlanId): Allowances {
  const terms = getPlanTerms(plan, 'v2-revamp')
  return {
    hours: terms.monthlyMeetingHours === null ? 'unlimited' : formatCount(terms.monthlyMeetingHours),
    credits: formatCount(terms.monthlyCredits),
  }
}

/** "45 minutes" under an hour, otherwise hours to one decimal place. */
function formatTimeLeft(hours: number): string {
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60))
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
  }
  const rounded = Math.round(hours * 10) / 10
  return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`
}

const planName = (plan: LineupPlanId) => getPlanTerms(plan, 'v2-revamp').label

/** The plan a banner speaks for, the plan it sells, and the reset date its limits name. */
export function limitCopyContext(
  banner: Pick<PlanBanner, 'ctaPlan'>,
  context: { paywall: PaywallState; subscription: SubscriptionInfo; now?: number },
): LimitCopyContext {
  const { paywall, subscription, now } = context
  return {
    plan: (paywall.plan === 'free' ? 'free' : (v2TierFor(paywall.plan) ?? 'free')) as LineupPlanId,
    upgradeTo: banner.ctaPlan,
    resets: resetDateLabel(subscription.currentPeriodEndAt, now === undefined ? undefined : new Date(now)),
  }
}

/** Whether a banner says the credits are spent, alone or with another limit. */
export function isCreditsOutBanner(banner: PlanBanner): boolean {
  return banner.kind === 'credits-out' || (banner.kind === 'limits-out' && Boolean(banner.limits?.includes('credits')))
}

/** What a banner says. Every figure comes from the revamp's plan terms, which match Orb. */
export function planBannerCopy(
  banner: PlanBanner,
  context: { paywall: PaywallState; subscription: SubscriptionInfo; now?: number },
): PlanBannerCopy {
  const { paywall, subscription } = context
  const limits = limitCopyContext(banner, context)
  const { plan } = limits
  const current = allowancesOf(plan)
  const free = allowancesOf('free')
  const target = allowancesOf(banner.ctaPlan)
  const targetName = planName(banner.ctaPlan)

  switch (banner.kind) {
    case 'plan-ended':
      return {
        title: `Your ${targetName} plan has ended`,
        body: `Renew to keep ${target.hours} hours of transcription and ${target.credits} credits a month.`,
        cta: `Renew ${targetName}`,
      }
    case 'trial-ending': {
      const trial = docksTrialEnding(paywall, subscription, context.now)
      const days = trial?.daysLeft ?? 0
      const when = days <= 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`
      return {
        title: `Your ${targetName} trial ends ${when}`,
        body: `Then you’ll move to Free, with ${free.hours} hours of transcription and ${free.credits} credits a month.`,
        cta: `Keep ${targetName}`,
      }
    }
    case 'trial-ended':
      return {
        title: `Your ${targetName} trial has ended`,
        body: `You’re on Free now, with ${free.hours} hours of transcription and ${free.credits} credits a month.`,
        cta: `Upgrade to ${targetName}`,
        stay: 'Continue on Free',
      }
    case 'limits-out':
      return combinedLimitsCopy(banner.limits ?? [], limits)
    case 'hours-out':
      return hoursOutCopy(limits)
    case 'credits-out':
      return creditsOutCopy(limits)
    case 'briefs-out':
      return briefsOutCopy(limits)
    case 'follow-ups-out':
      return followUpsOutCopy(banner.followUpLimit ?? 0, limits)
    case 'hours-low': {
      const left = formatTimeLeft(paywall.meetingHours.remaining ?? 0)
      return banner.action === 'upgrade'
        ? {
            title: `${left} of transcription left`,
            body: `Upgrade to ${targetName} for ${target.hours} hours a month.`,
            cta: `Upgrade to ${targetName}`,
          }
        : {
            title: `${left} of transcription left`,
            body: 'Buy more hours anytime. They never expire.',
            cta: 'Buy hours',
          }
    }
    case 'credits-low': {
      const left = `${formatCount(Math.max(0, Math.floor(paywall.credits.remaining)))} credits left`
      return banner.action === 'upgrade'
        ? {
            title: left,
            body: `Upgrade to ${targetName} for ${target.credits} credits a month.`,
            cta: `Upgrade to ${targetName}`,
          }
        : {
            title: left,
            body: `You’ve used most of this month’s ${current.credits} credits. Buy more anytime. They never expire.`,
            cta: 'Buy credits',
          }
    }
  }
}
